// src/ui/preview-mode.ts — Preview handoff helpers (P5 D-16, Pitfall 9).
//
// openPreviewTab is the canonical Admin → Game handoff. The admin SPA
// (Plan 05-08) calls it with the in-progress ContentBundle draft; the browser
// COPIES sessionStorage into the new tab at window.open() time, so the preview
// tab sees a snapshot of the draft and the admin tab keeps editing freely.
// (UI-SPEC §Preview-Mode Banner tooltip copy documents this for the admin.)
//
// Pitfall 9 (self-healing): isPreviewMode() returns true ONLY when BOTH the
// URL carries `?preview=draft` AND sessionStorage['hunter-content-draft'] is
// non-null. A stray ?preview=draft URL without a draft does not falsely
// activate preview mode. Unit tested in preview-mode.test.ts (P2/P3/P4).
//
// All sessionStorage accesses are wrapped in try/catch — sandboxed iframes
// throw on any storage access, and the DevOverlay precedent (sessionStorage
// reads guarded identically) is the house pattern.
//
// Source: .planning/phases/05-content-pipeline-characters-admin-cms/05-CONTEXT.md D-16;
//         .planning/phases/05-content-pipeline-characters-admin-cms/05-RESEARCH.md Pitfall 9.

import type { ContentBundle } from '../shared/content/types';

const DRAFT_KEY = 'hunter-content-draft';
const PREVIEW_QUERY = 'preview';
const PREVIEW_VALUE = 'draft';

/**
 * Write the draft bundle to sessionStorage and open the game in a new tab.
 *
 * The browser snapshots sessionStorage into the new tab at open() time, so
 * subsequent admin edits do NOT bleed into the preview tab — reopening Preview
 * is the way to see new edits (snapshot semantics, documented in UI-SPEC).
 */
export function openPreviewTab(draft: ContentBundle): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // sessionStorage may throw in sandboxed iframes / storage-disabled browsers.
    // If we cannot persist the draft, opening the tab is pointless — bail.
    return;
  }
  window.open('/?preview=draft', '_blank');
}

/**
 * True only when BOTH the URL query AND sessionStorage agree we are in preview.
 * Pitfall 9 self-healing — a stale ?preview=draft URL alone is not enough.
 */
export function isPreviewMode(): boolean {
  try {
    const hasQuery =
      new URLSearchParams(window.location.search).get(PREVIEW_QUERY) ===
      PREVIEW_VALUE;
    if (!hasQuery) return false;
    return sessionStorage.getItem(DRAFT_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Return the raw sessionStorage draft string, or null if absent or inaccessible.
 * Caller is responsible for JSON.parse + Zod validation — this helper stays
 * ignorant of schema so failures fall through to DEFAULT_BUNDLE cleanly.
 */
export function readPreviewDraft(): string | null {
  try {
    return sessionStorage.getItem(DRAFT_KEY);
  } catch {
    return null;
  }
}

/** Clear the stored draft. Called from admin after publish to avoid staleness. */
export function clearPreviewDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore — storage inaccessible; there is nothing to clear.
  }
}
